import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Search } from 'lucide-react';
import { OrgOnlineAdmissionsApi } from '@/services/org-online-admissions.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';

/**
 * Org tier — Manage Online Admission Classes (relocated from the Platform
 * console — see backend OrgOnlineAdmissionsService's doc comment). "Global
 * Actions" toggles one class NAME on/off across every school IN THIS ORG
 * that has it, not the whole platform; the per-school list below lets an
 * admin fine-tune one school at a time. Matches legacy's
 * manageClasses()/updateClasses()/bulkUpdateClasses().
 */
export function OrgManageOnlineAdmissionClassesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');

  const { data: summary, isFetching: loadingSummary } = useQuery({
    queryKey: ['org', 'online-admission-classes-summary'],
    queryFn: () => OrgOnlineAdmissionsApi.classesSummary(),
  });

  const { data: schoolsPage, isFetching: loadingSchools } = useQuery({
    queryKey: ['org', 'online-admission-classes', search],
    queryFn: () => OrgOnlineAdmissionsApi.listSchoolClasses({ search: search || undefined, limit: 20 }),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['org', 'online-admission-classes-summary'] });
    qc.invalidateQueries({ queryKey: ['org', 'online-admission-classes'] });
  };

  const bulkToggle = useMutation({
    mutationFn: ({ className, action }: { className: string; action: 'on' | 'off' }) => OrgOnlineAdmissionsApi.bulkToggle(className, action),
    onSuccess: invalidateAll,
  });

  const toggleSchoolClass = useMutation({
    mutationFn: ({ schoolId, classIds }: { schoolId: string; classIds: string[] }) => OrgOnlineAdmissionsApi.updateSchoolClasses(schoolId, classIds),
    onSuccess: invalidateAll,
  });

  return (
    <>
      <PageHeader title="Manage Online Admission Classes" description="Which classes are open for online admission across your organization's schools — globally by name, or per school." />

      <div className="card mb-6 p-5">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">Global Actions</h3>
        {loadingSummary && !summary ? (
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        ) : (
          <div className="flex flex-wrap gap-2">
            {(summary?.allClassNames ?? []).map((name) => {
              const enabled = summary?.enabledClassNames.includes(name);
              return (
                <button
                  key={name}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${
                    enabled ? 'border-green-200 bg-green-50 text-green-700' : 'border-slate-200 bg-slate-50 text-slate-600'
                  }`}
                  onClick={() => bulkToggle.mutate({ className: name, action: enabled ? 'off' : 'on' })}
                  disabled={bulkToggle.isPending}
                >
                  {name} {enabled ? '· On (click to disable everywhere)' : '· Off (click to enable everywhere)'}
                </button>
              );
            })}
            {summary && summary.allClassNames.length === 0 && <p className="text-sm text-slate-400">No classes found across your schools.</p>}
          </div>
        )}
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search schools" className="pl-9" />
      </div>

      <div className="space-y-3">
        {loadingSchools && !schoolsPage ? (
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        ) : (
          (schoolsPage?.rows ?? []).map((row) => (
            <SchoolClassesRowCard
              key={row.schoolId}
              schoolId={row.schoolId}
              schoolName={row.schoolName}
              classes={row.classes}
              saving={toggleSchoolClass.isPending}
              onSave={(classIds) => toggleSchoolClass.mutate({ schoolId: row.schoolId, classIds })}
            />
          ))
        )}
        {schoolsPage && schoolsPage.rows.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No schools found.</p>}
      </div>
    </>
  );
}

function SchoolClassesRowCard({
  schoolId,
  schoolName,
  classes,
  saving,
  onSave,
}: {
  schoolId: string;
  schoolName: string;
  classes: { id: string; name: string; enabled: boolean }[];
  saving: boolean;
  onSave: (classIds: string[]) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(classes.filter((c) => c.enabled).map((c) => c.id)));
  const dirty = classes.some((c) => c.enabled !== selected.has(c.id));

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="card p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900">{schoolName}</span>
          {selected.size === 0 && <Badge tone="amber">No classes open</Badge>}
        </div>
        {dirty && (
          <button className="text-xs font-medium text-brand-600 hover:text-brand-800 disabled:opacity-50" onClick={() => onSave([...selected])} disabled={saving}>
            Save
          </button>
        )}
      </div>
      {classes.length === 0 ? (
        <p className="text-xs text-slate-400">This school has no classes yet.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {classes.map((c) => (
            <label key={c.id} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600">
              <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
              {c.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
